'use client';

import { useTranslations } from 'next-intl';
import type { RenderMode } from '@/types/render';
import { cn } from '@/shared/lib/cn';

interface RenderModeToggleProps {
  mode: RenderMode;
  /** File URL of the carousel-selected render — gates Refine availability. */
  currentRenderImageUrl: string | null | undefined;
  /** Render id used as parent_render_id when Refine is selected. */
  currentRenderId: string | null | undefined;
  /**
   * Runs a finite attention pulse on the "New design" segment. Driven by the
   * amber nudge callout above the toggle: the banner tells the user what to do
   * and the pulse shows WHERE, so the two must fire together (see
   * `useDelayedNudge` for why both are held back for a few seconds after a
   * render lands). Never looping — see `.animate-mode-segment-pulse`.
   */
  pulseExplore?: boolean;
  onModeChange: (mode: RenderMode, parentRenderId: string | null) => void;
}

/**
 * Top-of-settings segmented control that switches the next render between
 * Explore (start from the angle photo) and Refine (iterate on the
 * carousel-selected render).
 *
 * The before/after preview lives in the on-page single-angle viewer, whose
 * compare slider is driven by the same `mode` (see `MainAngleDisplay` /
 * `DerivativeAngleCard`). So this control is a bare two-option toggle — no
 * preview of its own — mirroring Apple `RenderModeTogglePicker` and Android
 * `RenderModeToggle`.
 */
export function RenderModeToggle({
  mode,
  currentRenderImageUrl,
  currentRenderId,
  pulseExplore = false,
  onModeChange,
}: RenderModeToggleProps) {
  const t = useTranslations('render.refineMode');
  const isRefineAvailable = Boolean(currentRenderImageUrl && currentRenderId);

  // Segment renderers — pulled out so we can swap visual order without
  // duplicating handler logic. Logical order in JSX is [Recreate, Refine]
  // (LTR). RTL gets the mirrored visual order via the document reading
  // direction — no flex-row-reverse needed.
  const recreateSegment = (
    <button
      key="recreate"
      type="button"
      aria-pressed={mode === 'explore'}
      onClick={() => onModeChange('explore', null)}
      className={cn(
        'px-3 text-settings-row-title rounded-full transition-colors flex items-center justify-center',
        mode === 'explore'
          ? 'bg-segment-active text-foreground font-semibold shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
        // Pulse only while the segment is NOT selected: once it is, the pill's
        // own `shadow-sm` owns the box-shadow and there is nothing left to point
        // the user at.
        pulseExplore && mode !== 'explore' && 'animate-mode-segment-pulse'
      )}
    >
      {t('modeExplore')}
    </button>
  );

  const refineSegment = (
    <button
      key="refine"
      type="button"
      aria-pressed={mode === 'refine'}
      disabled={!isRefineAvailable && mode !== 'refine'}
      onClick={() => {
        if (isRefineAvailable && currentRenderId) {
          onModeChange('refine', currentRenderId);
        }
      }}
      className={cn(
        'px-3 text-settings-row-title rounded-full transition-colors flex items-center justify-center',
        mode === 'refine'
          ? 'bg-segment-active text-foreground font-semibold shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
        !isRefineAvailable &&
          mode !== 'refine' &&
          'opacity-50 cursor-not-allowed'
      )}
    >
      {t('modeRefine')}
    </button>
  );

  // Two buttons with `aria-pressed` — semantically a 2-option toggle. Order in
  // JSX (LTR reading): [Recreate | Refine] — matches the natural create →
  // iterate workflow. iOS segmented control look: contrast-white active pill on
  // a muted track. RTL inherits the document direction and visually mirrors the
  // order without flex-row-reverse. `w-fit mx-auto` shrinks the track to the
  // width its two labels need (segments are NOT `flex-1`, so they hug their
  // text) and centers it in the settings column on every viewport, phone
  // included — instead of stretching the full panel width. Matches Apple's
  // content-hugging, centered RenderModeTogglePicker. Identical sizing to the
  // Design/Blueprint picker (h-10, p-1, same active pill).
  return (
    <div className="flex w-fit max-w-full mx-auto rounded-full bg-settings-card border border-border p-1 h-10">
      {recreateSegment}
      {refineSegment}
    </div>
  );
}
